import React, { useMemo, useState, useEffect, useRef } from "react";
import "./MyEarningsDetails.css";

export default function MyEarningsDetails({ totalEarnings, lastMonthEarnings, earnings = {}, onBack, onApplyFilter, loadingData }) {
  const [startMonth, setStartMonth] = useState("");
  const [startYear, setStartYear] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [endYear, setEndYear] = useState("");

  const [appliedFilters, setAppliedFilters] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const dropdownRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const startYearOption = 2020;
  const years = Array.from({ length: currentYear - startYearOption + 1 }, (_, i) => currentYear - i);
  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFilterPanel(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleApplyFilter = () => {
    if (!startMonth || !startYear || !endMonth || !endYear) {
      alert("Please select both From and To Month/Year");
      return;
    }
    const getLastDayOfMonth = (y, mStr) => {
      const m = parseInt(mStr, 10);
      const d = new Date(y, m, 0);
      return d.getDate();
    };
    const lastDay = getLastDayOfMonth(Number(endYear), endMonth);
    const startStr = `${startYear}-${startMonth}-01T00:00:00.000Z`;
    const endStr = `${endYear}-${endMonth}-${String(lastDay).padStart(2, "0")}T23:59:59.999Z`;

    if (new Date(startStr) > new Date(endStr)) {
      alert("From date cannot be after To date");
      return;
    }

    setAppliedFilters({ start: new Date(startStr), end: new Date(endStr) });
    setShowFilterPanel(false);
    if (onApplyFilter) {
      onApplyFilter(startStr, endStr);
    }
  };

  const handleClearFilter = () => {
    setStartMonth("");
    setStartYear("");
    setEndMonth("");
    setEndYear("");
    setAppliedFilters(null);
    setShowFilterPanel(false);
    if (onApplyFilter) {
      onApplyFilter();
    }
  };

  const filteredTransactions = useMemo(() => {
    const apiTransactions = earnings.transactions || earnings.items || [];
    if (!Array.isArray(apiTransactions) || apiTransactions.length === 0) return [];

    let result = apiTransactions;

    if (appliedFilters) {
      const { start, end } = appliedFilters;
      result = result.filter((item) => {
        const date = new Date(item.created_at || item.createdAt);
        return date >= start && date <= end;
      });
    }

    return result.map((item, index) => ({
      id: item.id || index + 1,
      studentName: item.student_name || item.studentName || "Student",
      subjects: item.subjects || "Tuition",
      level: item.level || "",
      amount: Number(item.amount) || 0,
      status: item.status || "Credited",
      createdAt: item.created_at || item.createdAt || "",
      transferType: item.transfer_type || item.transferType || "RAZORPAY",
      manualMode: item.manual_mode || item.manualMode || null,
      manualRef: item.manual_transaction_ref || item.manualRef || null,
    }));
  }, [earnings, appliedFilters]);

  const displayTotalEarnings = useMemo(() => {
    if (appliedFilters) {
      return filteredTransactions.reduce((sum, item) => sum + item.amount, 0);
    }
    return totalEarnings;
  }, [filteredTransactions, totalEarnings, appliedFilters]);

  const formatINR = (value) => `₹${(Number(value) || 0).toLocaleString("en-IN")}`;

  return (
    <div className="my-earnings-page">
      <button type="button" className="my-earnings-back-btn" onClick={onBack}>
        ← Back
      </button>

      <div className="my-earnings-summary">{`My Earnings: ${formatINR(displayTotalEarnings)}`}</div>

      <div className="my-earnings-header-row">
        <div className="my-earnings-month">{`Last Month earnings: ${Number(lastMonthEarnings) || 0}`}</div>
        <div className="filter-dropdown-container" ref={dropdownRef}>
          <button
            type="button"
            className="my-earnings-filter-toggle-btn"
            onClick={() => setShowFilterPanel(!showFilterPanel)}
          >
            <span>Filter</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="calendar-icon">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </button>

          {showFilterPanel && (
            <div className="my-earnings-filter-panel dropdown">
              <h4 className="filter-panel-title">Filter by Range</h4>

              <div className="filter-field-group">
                <label className="filter-label">From</label>
                <div className="filter-selects-row">
                  <select
                    value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Month</option>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Year</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="filter-field-group">
                <label className="filter-label">To</label>
                <div className="filter-selects-row">
                  <select
                    value={endMonth}
                    onChange={(e) => setEndMonth(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Month</option>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={endYear}
                    onChange={(e) => setEndYear(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Year</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                className="my-earnings-apply-btn"
                type="button"
                onClick={handleApplyFilter}
              >
                Apply Filter
              </button>
              <button
                className="my-earnings-clear-btn"
                type="button"
                onClick={handleClearFilter}
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="my-earnings-content-grid">
        <div className="my-earnings-transactions">
          {loadingData ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}>
              <div className="loading">
                <img src="./book-loader.gif" alt=" Loading . . .  " />
              </div>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>No transactions found for this period.</div>
          ) : (
            filteredTransactions.map((item) => (
              <div className="earning-card" key={item.id}>
                <div className="earning-card-header">
                  <div className="earning-card-title-wrap">
                    <img src="./card-person-icon.svg" alt="student" />
                    <div className="earning-card-title">{item.studentName}</div>
                  </div>
                  <div className="earning-card-status">
                    {item.status}
                    <span className="earning-status-dot"></span>
                  </div>
                </div>

                {item.transferType === "MANUAL" && item.manualMode && (
                  <div className="earning-card-line">
                    <img src="./card-subject-icon.svg" alt="mode" />
                    <span>Mode: {item.manualMode === "BANK_TRANSFER" ? "Bank Transfer" : item.manualMode}</span>
                  </div>
                )}
                {item.manualRef && (
                  <div className="earning-card-line">
                    <img src="./card-subject-icon.svg" alt="ref" />
                    <span>Ref: {item.manualRef}</span>
                  </div>
                )}

                <div className="earning-card-footer">
                  <div className="earning-card-date">
                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                  </div>
                  <div className="earning-card-amount">{formatINR(item.amount)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
